/**
 * PocketFiler - Onboarding Welcome / Role Picker
 * First screen new users see after signup + email verification.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Home, Building2, Building, Landmark, Globe, Compass,
  ArrowRight, Loader2, Sparkles
} from 'lucide-react';

const ICONS = { Home, Building2, Building, Landmark, Globe, Compass };

// First action routes (where to send them after role pick)
const FIRST_ACTION_ROUTES = {
  individual: '/contract-templates',
  business: '/invite', // client invitation flow
  real_estate: '/deals',
  financial: '/clients',
  international: '/virtual-offices',
  explorer: '/dashboard',
};


export default function OnboardingWelcome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [useCases, setUseCases] = useState({});
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load use cases config
    api.get('/onboarding/config')
      .then(data => {
        setUseCases(data.use_cases || {});
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // If user already has a use case, skip to dashboard
    api.get('/onboarding/status')
      .then(status => {
        if (status.use_case) {
          navigate(FIRST_ACTION_ROUTES[status.use_case] || '/dashboard');
        }
      })
      .catch(() => {});
  }, [navigate]);

  const handleSelect = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post('/onboarding/role', { use_case: selected });
      toast.success(t('onboarding.welcome.roleSaved') || 'Great choice! Let\'s get started.');
      if (refreshUser) await refreshUser();
      // Route to their first action
      const route = FIRST_ACTION_ROUTES[selected] || '/dashboard';
      navigate(route + '?onboarding=1&use_case=' + selected);
    } catch (e) {
      toast.error(t('onboarding.welcome.error') || 'Something went wrong');
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            {t('onboarding.welcome.tag') || 'Welcome to PocketFiler'}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            {t('onboarding.welcome.greeting', { name: firstName }) || `Welcome, ${firstName} 👋`}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('onboarding.welcome.subtitle') || 'What brings you here today? Pick one to personalize your experience — takes 10 seconds.'}
          </p>
        </div>

        {/* Role cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {Object.entries(useCases).map(([key, config]) => {
            const Icon = ICONS[config.icon] || Compass;
            const isSelected = selected === key;
            return (
              <button
                key={key}
                onClick={() => setSelected(key)}
                className={`relative text-left p-5 rounded-xl border-2 transition-all hover:shadow-md ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-gray-200 dark:border-gray-800 hover:border-primary/40 bg-white dark:bg-gray-900'
                }`}
              >
                <div className={`inline-flex p-2.5 rounded-lg mb-3 ${
                  isSelected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                }`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-base mb-1">
                  {t(`onboarding.role.${key}.label`) || config.label}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                  {t(`onboarding.role.${key}.description`) || config.description}
                </p>
                <p className="text-xs text-primary font-medium">
                  → {t(`onboarding.role.${key}.firstAction`) || config.first_action_label}
                </p>
                {isSelected && (
                  <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    ✓
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <button
            onClick={handleSelect}
            disabled={!selected || submitting}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {t('onboarding.welcome.submitting') || 'Setting up...'}</>
            ) : (
              <>
                {selected
                  ? (t('onboarding.welcome.cta') || 'Continue')
                  : (t('onboarding.welcome.pickOne') || 'Pick one to continue')}
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
          <button
            onClick={handleSkip}
            className="text-sm text-muted-foreground hover:text-foreground px-4 py-2"
          >
            {t('onboarding.welcome.skip') || 'Skip for now'}
          </button>
        </div>

        {/* Footer hint */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          {t('onboarding.welcome.footer') || 'You can change this later in Settings.'}
        </p>
      </div>
    </div>
  );
}
